import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '@/api/client'
import type { QuotationVersionView } from '@/api/types'
import {
  ErrorBanner,
  formatDate,
  formatMoney,
  formatNumber,
  humanise,
  InlineSpinner,
} from '@/components/common'

interface PublicQuotation {
  quotationNumber: string
  status: string
  customerName: string
  eventDate: string
  serviceType: string
  eventType?: string
  guestCount: number
  totalAmount: number
  validUntil?: string
  decisionPending: boolean
  pdfDocumentId?: string
  version: QuotationVersionView
}

/**
 * The customer-facing quotation page. The random token in the URL is the
 * credential, so nothing here needs a login.
 */
export function QuotationAcceptPage() {
  const { token } = useParams<{ token: string }>()
  const [decision, setDecision] = useState<'ACCEPT' | 'REJECT' | null>(null)
  const [acceptedByName, setAcceptedByName] = useState('')
  const [remarks, setRemarks] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['public-quotation', token],
    queryFn: () => api.publicGet<PublicQuotation>(`/api/v1/public/quotations/${token}`),
    enabled: Boolean(token),
  })

  const respond = useMutation({
    mutationFn: (body: { decision: string; acceptedByName: string; remarks?: string }) =>
      api.publicPost(`/api/v1/public/quotations/${token}/decision`, body),
    onSuccess: async () => {
      setSubmitted(true)
      setDecision(null)
      await refetch()
    },
  })

  if (isLoading) return <InlineSpinner />

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="error">
          This quotation link is not valid or has expired. Please contact your event manager.
        </Alert>
      </Container>
    )
  }

  if (!data) return null
  const version = data.version

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: { xs: 3, md: 6 } }}>
      <Container maxWidth="md">
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography variant="overline" color="text.secondary">
            Quotation {data.quotationNumber}
          </Typography>
          <Typography variant="h4">Your event proposal</Typography>
          <Typography variant="body1" color="text.secondary">
            Prepared for {data.customerName} · {humanise(data.eventType)} on {formatDate(data.eventDate)}
          </Typography>
        </Stack>

        {submitted && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
            Thank you. Your decision has been recorded and your event manager has been notified.
          </Alert>
        )}

        {!data.decisionPending && !submitted && (
          <Alert severity="info" sx={{ mb: 3 }}>
            This quotation is {humanise(data.status).toLowerCase()}. Contact your event manager if you
            need a revised proposal.
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Detail label="Service" value={humanise(data.serviceType)} />
              <Detail label="Event date" value={formatDate(data.eventDate)} />
              <Detail label="Guests" value={formatNumber(data.guestCount)} />
              <Detail label="Valid until" value={formatDate(data.validUntil)} />
            </Grid>
          </CardContent>
        </Card>

        {version.menuSelections.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Your menu
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {version.menuSelections.map((selection) => (
                  <Chip key={selection.id} label={selection.itemName} variant="outlined" />
                ))}
              </Stack>
            </CardContent>
          </Card>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Charges
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {version.lines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>{line.description}</TableCell>
                    <TableCell align="right">
                      {formatNumber(line.quantity)} {humanise(line.unit)}
                    </TableCell>
                    <TableCell align="right">{formatMoney(line.lineTotal, true)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Divider sx={{ my: 2 }} />
            <Stack spacing={0.75} sx={{ maxWidth: 340, ml: 'auto' }}>
              <Row label="Subtotal" value={version.subtotalAmount} />
              {version.discountAmount > 0 && <Row label="Discount" value={-version.discountAmount} />}
              <Row label="Taxable value" value={version.taxableAmount} />
              <Row label="GST" value={version.taxAmount} />
              {version.roundOff !== 0 && <Row label="Round off" value={version.roundOff} />}
              <Divider />
              <Row label="Total payable" value={version.totalAmount} emphasise />
            </Stack>
          </CardContent>
        </Card>

        {version.terms.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Terms
              </Typography>
              <Stack component="ol" spacing={1} sx={{ pl: 2, m: 0 }}>
                {version.terms.map((term) => (
                  <Typography key={term.id} component="li" variant="body2">
                    {term.content}
                  </Typography>
                ))}
              </Stack>
            </CardContent>
          </Card>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button
            startIcon={<PictureAsPdfIcon />}
            variant="outlined"
            href={`/api/v1/public/quotations/${token}/pdf`}
            target="_blank"
          >
            Download the PDF
          </Button>
          {data.decisionPending && !submitted && (
            <>
              <Button variant="contained" size="large" onClick={() => setDecision('ACCEPT')}>
                Accept this quotation
              </Button>
              <Button color="inherit" onClick={() => setDecision('REJECT')}>
                Decline
              </Button>
            </>
          )}
        </Stack>

        <Dialog open={Boolean(decision)} onClose={() => setDecision(null)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {decision === 'ACCEPT' ? 'Accept this quotation' : 'Decline this quotation'}
          </DialogTitle>
          <DialogContent dividers>
            <ErrorBanner error={respond.error} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {decision === 'ACCEPT'
                ? 'Accepting confirms the menu, guest count and price shown above. Your event manager will follow up with the booking advance.'
                : 'Let us know why, and we will come back with a revised proposal.'}
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Your name"
                value={acceptedByName}
                onChange={(event) => setAcceptedByName(event.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Remarks"
                value={remarks}
                onChange={(event) => setRemarks(event.target.value)}
                multiline
                minRows={2}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDecision(null)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={respond.isPending || !acceptedByName.trim()}
              onClick={() =>
                respond.mutate({
                  decision: decision ?? 'ACCEPT',
                  acceptedByName: acceptedByName.trim(),
                  remarks: remarks || undefined,
                })
              }
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  )
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Grid item xs={6} sm={3}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </Grid>
  )
}

function Row({
  label,
  value,
  emphasise = false,
}: {
  label: string
  value: number
  emphasise?: boolean
}) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant={emphasise ? 'subtitle1' : 'body2'} color="text.secondary">
        {label}
      </Typography>
      <Typography variant={emphasise ? 'subtitle1' : 'body2'} fontWeight={emphasise ? 700 : 500}>
        {formatMoney(value, true)}
      </Typography>
    </Stack>
  )
}
